import React, { useState, useEffect, useCallback, FC } from "react";
import { useParams } from "react-router-dom";
import { useApolloClient } from "@apollo/client";
import { useTranslation } from "react-i18next";
import cx from "classnames";
import { Select } from "@foris/avocado-suite";
import { buildingQuery } from "../graphql/building.queries";
import { Building, DataQuery } from "@models/ISchema";
import { IParams } from "@models/IParams";
import { ISelectorBuilding, IOption } from "./ISelectors";
import css from "./selectors.module.scss";

interface BuildingSelectProps extends Omit<ISelectorBuilding, "label"> {
  label?: boolean;
  labelValue?: string;
}

const BuildingSelect: FC<BuildingSelectProps> = props => {
  const client = useApolloClient();
  const { t } = useTranslation();
  const { onCallback, campus, label, className, placeholder } = props;
  const { scenario }: IParams = useParams();
  const [optionsBuilding, setOptionsBuilding] = useState<IOption[]>([]);
  const [valueBuilding, setValueBuilding] = useState(null);
  const [prevCampus, setPrevCampus] = useState(campus);
  const [disabled, setDisabled] = useState(false);

  const getBuildingOptions = useCallback(async () => {
    try {
      const response = await client.query({
        query: buildingQuery,
        variables: {
          scenarioId: scenario,
          filter: { fields: { campusId: { eq: campus } } },
        },
      });
      const dataQuery: DataQuery = response?.data?.data;
      const optBuildings =
        dataQuery?.buildings?.items?.map((building: Building) => ({
          label: `${building?.code ? `${building?.code}, ` : ""}${building?.name}`,
          value: building.id,
        })) ?? [];
      setOptionsBuilding(optBuildings);
    } catch (error) {
      console.error(error);
    }
  }, [client, scenario, campus]);

  useEffect(() => {
    if (campus && prevCampus !== campus && disabled) {
      getBuildingOptions();
      setPrevCampus(campus);
      setDisabled(false);
    }
    if (!campus) {
      setPrevCampus(null);
      setValueBuilding("");
      setDisabled(true);
    }
  }, [campus, prevCampus, disabled, getBuildingOptions]);

  return (
    <div className={cx(css.selectors, className)}>
      <Select
        label={
          label
            ? t("assignation-edit.advanced-search.classroom.form.selectors.building")
            : undefined
        }
        options={optionsBuilding}
        value={valueBuilding}
        placeholder={placeholder ? placeholder : label ? "Seleccionar" : "Edificio"}
        isClearable
        disabled={disabled}
        onChange={(value: IOption) => {
          if (value) {
            setValueBuilding(value);
            onCallback(value);
          } else {
            setValueBuilding("");
            onCallback(value);
          }
        }}
      />
    </div>
  );
};

export default BuildingSelect;
